/**
 * E2E renderer test probe — lets Playwright drive the onboarding permissions
 * modal's camera/microphone/network Allow/Deny outcomes without ever
 * triggering a real OS permission prompt.
 *
 * Per `think-in-systems` (ai.knowledge/global): a primitive, mirroring
 * `calibration-probe.ts`'s shape. WHY this exists: camera/microphone
 * permission requests call `askForMediaAccess` (studio/electron
 * `permissions.ts`), which opens a native OS TCC dialog that Playwright/CDP
 * cannot dismiss (see `add-source.helpers.ts`'s NATIVE-PROMPT CAVEAT — the
 * same constraint this probe routes around). `electron/api.ts`'s
 * `permissions.requestMedia`/`requestNetwork`/`mediaStatus`/`networkStatus`
 * consult this probe first; when a key has been probed they short-circuit
 * before ever reaching the real IPC channel, so the real
 * `useGrantSuccessTracking`/`permissions.tsx` decision logic still runs
 * unmodified against these faked inputs — only the OS boundary is faked.
 *
 * `status` queries fall through to the real IPC until EITHER a request has
 * been probed for that key OR the test explicitly pins a starting status via
 * `__irisSetPermissionStatus` — the latter exists because this modal reuses
 * the real, persistent userData profile (own-launch, no temp dir), so a prior
 * real grant on the CI machine could otherwise make the mount-time state
 * nondeterministic. Pinning the three keys to a known "not yet decided"
 * status before the modal mounts makes the test's starting condition
 * independent of the CI machine's actual OS permission history.
 */

export type PermissionProbeKey = 'camera' | 'microphone' | 'network';

const requestOutcomes = new Map<PermissionProbeKey, boolean>();
const decidedStatus = new Map<PermissionProbeKey, boolean>();

/**
 * The faked status for `key`, if a request has already been probed for it.
 * `undefined` means "let the real IPC status query answer" — outside e2e
 * this always returns `undefined` since nothing ever populates the map.
 */
export const getPermissionProbeStatus = (key: PermissionProbeKey): boolean | undefined =>
  decidedStatus.get(key);

/**
 * The faked outcome for the NEXT `request` call on `key`, if one was set via
 * `__irisSetPermissionRequestOutcome`. Consuming it also writes through to
 * `decidedStatus`, matching the real OS: once a permission is decided,
 * subsequent status queries reflect that decision.
 */
export const getPermissionProbeRequestOutcome = (key: PermissionProbeKey): boolean | undefined => {
  const outcome = requestOutcomes.get(key);
  if (outcome === undefined) {
    return undefined;
  }
  decidedStatus.set(key, outcome);
  return outcome;
};

/**
 * Renderer-process gate. Per `calibration-probe.ts`'s identical rationale:
 * `process.env` is NOT populated in this renderer even with
 * `nodeIntegration: true` + `contextIsolation: false` — only the preload
 * bridge (`window.electron.process.env`) carries the real inherited env.
 *
 * Checked live inside the setter below (not once at import time) so nothing
 * outside e2e can ever write into the map — no real user or production code
 * path calls `globalThis.__irisSetPermissionRequestOutcome` regardless, but
 * this keeps the guarantee independent of import ordering.
 */
const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

const setRequestOutcome = (key: PermissionProbeKey, granted: boolean): void => {
  if (!isE2eEnabled()) {
    return;
  }
  requestOutcomes.set(key, granted);
};

/** Pins `key`'s status directly, independent of any request outcome. */
const setStatus = (key: PermissionProbeKey, granted: boolean): void => {
  if (!isE2eEnabled()) {
    return;
  }
  decidedStatus.set(key, granted);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(globalThis as any).__irisSetPermissionRequestOutcome = setRequestOutcome;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(globalThis as any).__irisSetPermissionStatus = setStatus;

/** Test-only: clears all probed state between unit tests (module state is a singleton). */
export const resetPermissionProbeForTests = (): void => {
  requestOutcomes.clear();
  decidedStatus.clear();
};
